import {
  addGameObject,
  anchoredPos,
  getAbsolutePosAndScale,
  getZeroStaticEntity,
  setParent,
} from '../entity';
import { colorToHex, v2 } from '../helpers';
import { isMouseDown, mouseDownThisFrame, setVisibilityState } from '../simulate';
import {
  CANVAS_TEXT_CAP_MARGIN_RATIO,
  DEFAULT_TEXT_STYLE,
  SHAPE_STYLES,
  TEXTOBJ2_HANDLE_SIZE,
  TEXTOBJ2_MIN_HEIGHT,
  TEXTOBJ2_MIN_WIDTH,
} from '../constants';
import * as Container from '../container';
import { COLORS } from '../constants-colors';

import {
  ServerEvent_EntityProps,
  ObjectType,
  type ServerEvent_TextObject2,
} from '../shared/api_shared_types';
import {
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  GameObject,
  Shape,
  V2,
  type TextObject2,
  RenderItemType,
  TextAlignHor,
} from '../types';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackagePoint,
  type RenderPackageRect,
  type RenderPackageText,
} from '../render_package';
import * as TextLayoutController from '../controllers/text_layout_controller';
import { setMouseCursor, resetMouseCursor } from '../window_layout';
import { TextObject2EditMode } from '../textobject/textobject2_types';
import logger from '../controllers/logger';
export { TextObject2EditMode } from '../textobject/textobject2_types';

export function controller(): GameObjectController {
  return {
    getNew: getZeroTextObject2,
    sync,
    update,
    createPackage,
    dispose,
  };
}

function dispose(obj: GameObject) {
  const tObj = obj as TextObject2;
  if (tObj.editTextArea !== null) {
    tObj.editTextArea.remove();
    tObj.editTextArea = null;
  }
  tObj._isEditing = false;
  tObj._pendingRemoval = false;
  tObj._dragSelectionAnchor = null;
  if (tObj._hasTextCursor) {
    tObj._hasTextCursor = false;
    resetMouseCursor();
  }
}

export function getZeroTextObject2(ent: Entity) {
  let newTextObject: TextObject2 = {
    ...ent,
    objectType: ObjectType.TEXTOBJECT2,
    textOverflow: 'ellipsis',
    editMode: TextObject2EditMode.STATIC,

    defaultTextStyle: DEFAULT_TEXT_STYLE,

    lines: [],

    text: '',
    maxLength: 0,
    canEditSize: false,
    handle: null,
    padding: [40, 40, 40, 40],
    editTextArea: null,

    _wrapWidth: 0,
    _editAreaDirty: false,
    _isEditing: false,
    _pendingRemoval: false,
    _pendingFocus: false,
    _pendingCaretOffset: 0,
    _dragSelectionAnchor: null,
    _hasTextCursor: false,
  };

  return newTextObject;
}

function sync(manager: GameManager, textObject: TextObject2, data: ServerEvent_TextObject2) {
  let entityProps: ServerEvent_EntityProps = {};

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  if (data.hasOwnProperty('width')) {
    textObject.width = data.width;
  }

  if (data.hasOwnProperty('height')) {
    textObject.height = data.height;
  }

  if (data.hasOwnProperty('wrapText')) {
    textObject.textOverflow = data.wrapText ? 'wrap' : '';
  }

  if (!textObject.id) {
    addGameObject(manager, textObject);
    initTextObject(manager, textObject);

    // setParentByEntityProps(manager, textObject, data.sId, data.roomId, entityProps);
  }

  if ('text' in data) {
    if (textObject.text !== data.text) {
      setText(textObject, data.text);
    }
  }
}

export function initTextObject(manager: GameManager, textObject: TextObject2): TextObject2 {
  if (textObject.shape === Shape.NONE) {
    textObject.shape = Shape.RECT;
  }

  textObject.allowHighlight = true;
  textObject.clickable = true;
  textObject.allowDrag = false;

  setVisibilityState(textObject, VisibilityState.DEFAULT);

  const handleEnt = getZeroStaticEntity();
  handleEnt.width = TEXTOBJ2_HANDLE_SIZE;
  handleEnt.height = TEXTOBJ2_HANDLE_SIZE;
  handleEnt.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(0, 0), v2(0.5, 0.5));
  const handle = Container.createAndAddLocalContainer(manager, handleEnt);
  setParent(handle, textObject);
  textObject.handle = handle;

  return textObject;
}

export function createAndAddLocalTextObject2(
  manager: GameManager,
  ent: Entity,
  props: Partial<TextObject2>,
): TextObject2 {
  const tObj = {
    ...getZeroTextObject2(ent),
    ...props,
  };

  addGameObject(manager, tObj);

  initTextObject(manager, tObj);

  if (props.text) {
    setText(tObj, props.text, true);
  }

  return tObj;
}

function breakLongWord(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  word: string,
): string {
  const innerWidth = tObj.width - tObj.padding[1] - tObj.padding[3];
  let remaining = word;

  for (let guard = 0; guard < word.length; guard++) {
    const remainingWidth = TextLayoutController.getTextWidth(
      ctx,
      tObj.defaultTextStyle.fontId,
      tObj.defaultTextStyle.fontSize,
      remaining,
    );
    if (remainingWidth <= innerWidth) {
      return remaining;
    }

    let lo = 0;
    let hi = remaining.length - 1;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const w = TextLayoutController.getTextWidth(
        ctx,
        tObj.defaultTextStyle.fontId,
        tObj.defaultTextStyle.fontSize,
        remaining.slice(0, mid),
      );
      if (w <= innerWidth) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }

    const cutAt = lo > 0 ? lo : 1;
    pushLine(tObj, ctx, remaining.slice(0, cutAt), tObj.height);
    remaining = remaining.slice(cutAt);
  }

  return remaining;
}

function pushLine(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  lineText: string,
  yPosition: number,
) {
  const width = TextLayoutController.getTextWidth(
    ctx,
    tObj.defaultTextStyle.fontId,
    tObj.defaultTextStyle.fontSize,
    lineText,
  );
  const height = tObj.defaultTextStyle.fontSize * tObj.defaultTextStyle.lineHeight;
  tObj.lines.push({ text: lineText, visibleText: lineText, width, height, yPosition });
  tObj.height += height;
  logger.merijn({ yPosition, height, total: tObj.height }, tObj.defaultTextStyle);
}

function tokenizeParagraph(paragraph: string): string[] {
  const tokens: string[] = [];
  for (const spacePart of paragraph.split(' ')) {
    const dashParts = spacePart.split('-');
    for (let d = 0; d < dashParts.length - 1; d++) {
      tokens.push(dashParts[d] + '-');
    }
    tokens.push(dashParts[dashParts.length - 1]);
  }
  return tokens;
}

function joinTokens(tokens: string[]): string {
  let result = tokens[0] || '';
  for (let i = 1; i < tokens.length; i++) {
    result += tokens[i - 1].endsWith('-') ? tokens[i] : ' ' + tokens[i];
  }
  return result;
}

export function setText(tObj: TextObject2, text: string, force = false) {
  if (!force && text === tObj.text) return;

  const ctx = tObj.manager.textRenderCtx;
  const setHeight = tObj.height;
  tObj.text = text;
  tObj.lines = [];
  tObj.height = tObj.padding[0];

  if (tObj.textOverflow === 'wrap') {
    const innerWidth = tObj.width - tObj.padding[1] - tObj.padding[3];
    const paragraphs = text.split('\n');

    for (let i = 0; i < paragraphs.length; i++) {
      const words = tokenizeParagraph(paragraphs[i]);
      const currentWords: string[] = [];

      for (let w = 0; w < words.length; w++) {
        const candidate =
          currentWords.length === 0 ? words[w] : joinTokens([...currentWords, words[w]]);
        const candidateWidth = TextLayoutController.getTextWidth(
          ctx,
          tObj.defaultTextStyle.fontId,
          tObj.defaultTextStyle.fontSize,
          candidate,
        );

        if (candidateWidth <= innerWidth) {
          currentWords.push(words[w]);
        } else if (currentWords.length === 0) {
          const remainder = breakLongWord(tObj, ctx, words[w]);
          if (remainder.length > 0) {
            currentWords.push(remainder);
          }
        } else {
          pushLine(tObj, ctx, joinTokens(currentWords), tObj.height);

          currentWords.length = 0;
          const wordWidth = TextLayoutController.getTextWidth(
            ctx,
            tObj.defaultTextStyle.fontId,
            tObj.defaultTextStyle.fontSize,
            words[w],
          );
          if (wordWidth > innerWidth) {
            const remainder = breakLongWord(tObj, ctx, words[w]);
            if (remainder.length > 0) {
              currentWords.push(remainder);
            }
          } else {
            currentWords.push(words[w]);
          }
        }
      }

      if (currentWords.length > 0) {
        pushLine(tObj, ctx, joinTokens(currentWords), tObj.height);
      }
    }

    tObj.height += tObj.padding[2];
    tObj._wrapWidth = tObj.width;
  } else {
    const textLines = text.split('\n');
    let maxTextWidth = 0;

    for (let i = 0; i < textLines.length; i++) {
      const yPosition = tObj.height;
      const width = TextLayoutController.getTextWidth(
        ctx,
        tObj.defaultTextStyle.fontId,
        tObj.defaultTextStyle.fontSize,
        textLines[i],
      );
      const height = tObj.defaultTextStyle.fontSize * tObj.defaultTextStyle.lineHeight;

      tObj.height += height;
      if (width > maxTextWidth) {
        maxTextWidth = width;
      }

      tObj.lines.push({
        text: textLines[i],
        visibleText: textLines[i],
        width,
        height,
        yPosition,
      });
    }

    tObj.width = maxTextWidth + tObj.padding[1] + tObj.padding[3];
    tObj.height += tObj.padding[2];

    if (setHeight > tObj.height) {
      tObj.height = setHeight;
    }
  }

  tObj.isDirty = true;
}

function computeCaretOffset(tObj: TextObject2): number {
  if (tObj.lines.length === 0) {
    return tObj.text.length;
  }

  const innerClickX = tObj.relativeMousePos.x + tObj.width / 2 - tObj.padding[3];
  const innerClickY = tObj.relativeMousePos.y + tObj.height / 2 - tObj.padding[0];

  let lineIndex = 0;
  for (let i = 0; i < tObj.lines.length; i++) {
    if (tObj.lines[i].yPosition - tObj.padding[0] <= innerClickY) {
      lineIndex = i;
    } else {
      break;
    }
  }

  const ctx = tObj.manager.textRenderCtx;
  const { fontId, fontSize } = tObj.defaultTextStyle;
  const lineText = tObj.lines[lineIndex].text;

  let lo = 0; // the lowest index that still fits within innerClickX (i.e., the text up to lo chars wide ≤ click X)
  let hi = lineText.length; // the highest index that might still fit

  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (
      TextLayoutController.getTextWidth(ctx, fontId, fontSize, lineText.slice(0, mid)) <=
      innerClickX
    ) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }

  if (lo < lineText.length) {
    const wLo = TextLayoutController.getTextWidth(ctx, fontId, fontSize, lineText.slice(0, lo));
    const wNext = TextLayoutController.getTextWidth(
      ctx,
      fontId,
      fontSize,
      lineText.slice(0, lo + 1),
    );
    if (Math.abs(innerClickX - wNext) < Math.abs(innerClickX - wLo)) {
      lo++;
    }
  }

  let pos = 0;
  for (let i = 0; i < lineIndex; i++) {
    const found = tObj.text.indexOf(tObj.lines[i].text, pos);
    if (found !== -1) {
      pos = found + tObj.lines[i].text.length;
    }
  }
  const lineStart = tObj.text.indexOf(tObj.lines[lineIndex].text, pos);
  const resolvedLineStart = lineStart !== -1 ? lineStart : pos;
  return resolvedLineStart + lo;
}

function stopTextEdit(tObj: TextObject2) {
  if (tObj.editTextArea === null) return;
  setText(tObj, tObj.editTextArea.value);
  tObj._isEditing = false;
  tObj._dragSelectionAnchor = null;
  tObj.editTextArea.style.pointerEvents = 'none';
  tObj._editAreaDirty = true;
  tObj.manager.cursor.allowTyping = true;
}

function startTextEdit(tObj: TextObject2) {
  if (tObj.editTextArea !== null) return;

  const el = document.createElement('textarea');
  el.classList.add('text-editor');

  const fontSize = tObj.defaultTextStyle.fontSize;
  const lineHeightPx = fontSize * tObj.defaultTextStyle.lineHeight;
  el.style.fontSize = `${fontSize}px`;
  el.style.lineHeight = `${lineHeightPx}px`;
  el.style.fontFamily = tObj.defaultTextStyle.fontId;
  el.style.color = colorToHex(tObj.defaultTextStyle.color);
  el.style.textAlign = 'left';
  el.style.resize = 'none';
  el.style.background = 'transparent';
  el.style.border = 'none';
  el.style.outline = 'none';
  el.style.padding = '0';
  el.style.margin = '0';
  el.style.overflow = 'hidden';
  el.style.position = 'absolute';
  el.style.transformOrigin = 'top left';
  el.style.boxSizing = 'content-box';

  el.readOnly = tObj.editMode === TextObject2EditMode.OPEN_TO_SELECT;
  el.value = tObj.text;
  if (tObj.maxLength > 0) {
    el.maxLength = tObj.maxLength;
  }

  const caretOffset = computeCaretOffset(tObj);

  tObj._isEditing = true;
  tObj.manager.cursor.allowTyping = false;

  el.addEventListener('blur', () => stopTextEdit(tObj));

  tObj.manager.DOM.overlayContainer.appendChild(el);
  tObj.editTextArea = el;

  tObj._pendingFocus = true;
  tObj._pendingCaretOffset = caretOffset;
}

function update(tObj: TextObject2): boolean {
  let isDirty = false;

  if (tObj._pendingFocus && tObj.editTextArea !== null) {
    tObj._pendingFocus = false;
    tObj.editTextArea.focus({ preventScroll: true });
    tObj.editTextArea.setSelectionRange(tObj._pendingCaretOffset, tObj._pendingCaretOffset);
    if (
      tObj.editMode === TextObject2EditMode.OPEN_TO_EDIT ||
      tObj.editMode === TextObject2EditMode.OPEN_TO_SELECT
    ) {
      tObj._dragSelectionAnchor = tObj._pendingCaretOffset;
    }
  }

  if (tObj._isEditing && tObj._dragSelectionAnchor !== null && tObj.editTextArea !== null) {
    if (isMouseDown()) {
      const anchor = tObj._dragSelectionAnchor;
      const [absPos, absScale] = getAbsolutePosAndScale(tObj);
      tObj.relativeMousePos.x = (tObj.manager.mousePos.x - absPos.x) / absScale;
      tObj.relativeMousePos.y = (tObj.manager.mousePos.y - absPos.y) / absScale;
      const current = computeCaretOffset(tObj);
      const selStart = Math.min(anchor, current);
      const selEnd = Math.max(anchor, current);
      const direction = anchor <= current ? 'forward' : 'backward';
      tObj.editTextArea.setSelectionRange(selStart, selEnd, direction);
    } else {
      tObj._dragSelectionAnchor = null;
    }
  }

  if (
    tObj.editMode === TextObject2EditMode.OPEN_TO_EDIT ||
    tObj.editMode === TextObject2EditMode.OPEN_TO_SELECT
  ) {
    if (mouseDownThisFrame() && tObj.mouseDownOnMe) {
      startTextEdit(tObj);
      isDirty = true;
    }
  } else if (tObj.editMode === TextObject2EditMode.CLICK_TO_EDIT) {
    if (tObj.clickedThisFrame) {
      startTextEdit(tObj);
      isDirty = true;
    }
  }

  if (
    tObj.editMode === TextObject2EditMode.OPEN_TO_EDIT ||
    tObj.editMode === TextObject2EditMode.OPEN_TO_SELECT
  ) {
    if (tObj.highlighted && !tObj._hasTextCursor) {
      tObj._hasTextCursor = true;
      setMouseCursor('text');
    } else if (!tObj.highlighted && tObj._hasTextCursor) {
      tObj._hasTextCursor = false;
      resetMouseCursor();
    }
  }

  if (tObj._pendingRemoval) {
    tObj._pendingRemoval = false;
    if (tObj.editTextArea !== null) {
      tObj.editTextArea.remove();
      tObj.editTextArea = null;
    }
  }

  if (tObj._editAreaDirty) {
    tObj._editAreaDirty = false;
    tObj._pendingRemoval = true;
    isDirty = true;
  }

  if (tObj.textOverflow === 'wrap' && tObj.width !== tObj._wrapWidth) {
    setText(tObj, tObj.text, true);
    isDirty = true;
  }

  const handle = tObj.handle;
  if (handle !== null) {
    if (tObj.canEditSize !== handle.allowDrag) {
      handle.allowDrag = tObj.canEditSize;
      handle.allowHighlight = tObj.canEditSize;
      if (tObj.canEditSize) {
        Container.setContainerStyle(handle, { ...SHAPE_STYLES.NONE, fillColor: COLORS.WHITE_100 });
      } else {
        Container.setContainerStyle(handle, null);
      }
    }

    if (tObj.canEditSize) {
      if (handle.dragged) {
        const newWidth = Math.max(TEXTOBJ2_MIN_WIDTH, handle.anchoredPos.offset.x * 2);
        const newHeight = Math.max(TEXTOBJ2_MIN_HEIGHT, handle.anchoredPos.offset.y * 2);
        tObj.width = newWidth;
        tObj.height = newHeight;
        isDirty = true;
      } else {
        handle.anchoredPos.offset.x = tObj.width / 2;
        handle.anchoredPos.offset.y = tObj.height / 2;
      }
    }
  }

  return isDirty;
}

export function createPackage(
  textObject: TextObject2,
  position: V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  if (textObject.canEditSize) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: textObject.width,
      height: textObject.height,
      opacity: textObject.opacity,

      shapeStyle: { ...SHAPE_STYLES.NONE, strokeWeight: 1, strokeColor: COLORS.WHITE_100 },
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };

    rPackage.push(rect);
  }

  if (textObject._isEditing) {
    return rPackage;
  }

  for (let i = 0; i < textObject.lines.length; i++) {
    const positionY =
      position.y -
      scale * textObject.height * 0.5 +
      scale * textObject.defaultTextStyle.fontSize * CANVAS_TEXT_CAP_MARGIN_RATIO +
      scale * textObject.lines[i].yPosition;

    let rText: RenderPackageText = {
      type: RenderItemType.text,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x - scale * textObject.width * 0.5 + scale * textObject.padding[3],
      positionY,
      hAlign: TextAlignHor.LEFT,
      fillColor: textObject.defaultTextStyle.color,
      strokeWeight: 0,
      strokeColor: [0, 0, 0],
      size: textObject.defaultTextStyle.fontSize,
      text: textObject.lines[i].visibleText,
      fontId: textObject.defaultTextStyle.fontId,
      shadow: null,
      opacity: 1,
    };
    rPackage.push(rText);

    if (false) {
      let p: RenderPackagePoint = {
        type: RenderItemType.point,
        ...EMPTY_RENDER_TRANSFORM,
        positionX: position.x - scale * textObject.width * 0.5 + scale * textObject.padding[3],
        positionY,
        size: 4,
        color: COLORS.RED_SALSA,
      };
      rPackage.push(p);
    }
  }

  return rPackage;
}

// Returns the caret position right after the last character of the last line,
// as a V2 relative to the textObject's space (origin at its center).
export function getEndCaretPosition(tObj: TextObject2): V2 {
  const leftX = -tObj.width / 2 + tObj.padding[3];

  if (tObj.lines.length === 0) {
    return v2(leftX, -tObj.height / 2 + tObj.padding[0]);
  }

  const lastLine = tObj.lines[tObj.lines.length - 1];
  return v2(leftX + lastLine.width, lastLine.yPosition + lastLine.height);
}
